import React, { useEffect } from 'react';
import clsx from 'clsx';

import { Input, InputProps } from './Input';
import { DivPropsWithoutRef } from '../../common/types';

export type IRichInputProps<
  BaseProps extends { classes?: any; error?: boolean }
> = Omit<BaseProps, 'rootClassName' | 'className' | 'error' | 'classes'> & {
  /**
   * Компонент
   * @type React.ElementType
   */
  component?: React.ElementType<BaseProps>;
  /**
   * Подпись/наименование
   */
  label?: React.ReactNode;
  /**
   * Текст ошибки
   */
  errorMessage?: React.ReactNode;
  /**
   * Текст подсказки
   */
  hint?: React.ReactNode;
  /**
   * Дополнительные классы элементов компонента:
   * * root - контейнер
   * * input - класс для component
   * * label - лейбл
   * * error - сообщение ошибки
   * * message – сообщение (`hint` или `errorMessage`)
   */
  classes?: {
    input?: BaseProps['classes'];
    root?: string;
    label?: string;
    error?: string;
    message?: string;
  };
  /**
   * Дополнительный css-класс корневого элемента
   */
  className?: string;
  /**
   * Дополнительные стили корневого элемента
   */
  style?: React.CSSProperties;
  /**
   * Дополнительные свойства корневого элемента
   */
  rootProps?: DivPropsWithoutRef;
};

export type RichInputPropsDefault = Omit<
  IRichInputProps<InputProps>,
  'component'
> & {
  component?: typeof Input;
};

export function RichInput<BaseProps>(props: IRichInputProps<BaseProps>);
export function RichInput(props: RichInputPropsDefault);
export function RichInput<BaseProps>({
  component = Input,
  label,
  errorMessage,
  hint,
  className,
  classes,
  style,
  rootProps,
  ...baseInputProps
}: IRichInputProps<BaseProps> | RichInputPropsDefault) {
  // Чтобы ts не проверял корректность передачи пропов
  const Component = component as React.ElementType;
  const hasMessage = Boolean(errorMessage) || Boolean(hint);
  return (
    <div
      {...rootProps}
      style={{ ...style, ...rootProps?.style }}
      className={clsx(
        'hse-RichInput',
        { 'hse-RichInput_hasError': Boolean(errorMessage) },
        className,
        classes?.root,
        rootProps?.className
      )}
    >
      {Boolean(label) && (
        <div className={clsx('hse-RichInput__label', classes?.label)}>
          {label}
        </div>
      )}
      <Component
        {...baseInputProps}
        error={Boolean(errorMessage)}
        classes={classes?.input}
      />
      {hasMessage && (
        <div
          className={clsx(
            'hse-RichInput__message',
            classes?.message,
            errorMessage && classes?.error
          )}
        >
          {errorMessage || hint}
        </div>
      )}
    </div>
  );
}
